import { Suspense } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { log } from '@eos-web/shared-module';

import { useOnlineStatus } from '@leagueplatform/web-common';
import {
  FullPageError,
  LoadingIndicator,
  ErrorState,
} from '@leagueplatform/web-common-components';
import { LeagueProvider } from '@leagueplatform/core';
import { ObservabilityErrorBoundary } from '@leagueplatform/observability';

import { initMasonryRegistry } from 'utils/init-masonry-registry';
import { initMasonryActionHandlers } from 'utils/init-masonry-action-handlers';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { EnvironmentSwitcher } from 'components/environment-switcher/environment-switcher.component';
import { config } from 'config';
import { appRoutes } from 'app-routes';

initMasonryRegistry();
initMasonryActionHandlers();

log('test message');

// Routes where the dev environment switcher is hidden (matches the exact path
// or any sub-route). Empty = visible everywhere. '/guest' covers the HRA member-
// outreach screens, where the env is pinned to test4-ca (see config.tsx) so
// switching there has no effect.
const ENV_SWITCHER_EXCLUDED_ROUTES: string[] = ['/guest'];

const LeagueProviderWrapper = () => {
  const { isOffline, isOnline } = useOnlineStatus();
  return (
    <LeagueProvider config={config}>
      <ObservabilityErrorBoundary
        fallback={<ErrorState error={null} />}
        errorContext={{
          context: { Context: { location: 'Root App ErrorBoundary' } },
        }}
      >
        <Suspense fallback={<LoadingIndicator />}>
          {isOffline && (
            <FullPageError error={null} route={APP_MANAGED_ROUTES.HOME} />
          )}
          {isOnline && <Outlet />}
        </Suspense>
      </ObservabilityErrorBoundary>
      <ReactQueryDevtools initialIsOpen={false} />
      <EnvironmentSwitcher excludedRoutes={ENV_SWITCHER_EXCLUDED_ROUTES} />
    </LeagueProvider>
  );
};

const router = createBrowserRouter([
  {
    element: <LeagueProviderWrapper />,
    children: appRoutes,
  },
]);

export const App = () => <RouterProvider router={router} />;
